import type { ComponentProps, CSSProperties } from 'react';
import { Icon, type CupertinoIconName } from '../icons/Icon';
import { Spinner } from './Spinner';
import styles from './Button.module.css';

export type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'elevated';
export type ButtonTone = 'primary' | 'error' | 'neutral';

export interface ButtonProps extends ComponentProps<'button'> {
  /** Material 3 button type (themed with the Plume ColorScheme). */
  variant?: ButtonVariant;
  /** `error` = destructive colours; `neutral` = onSurface text (Filter "Clear"). */
  tone?: ButtonTone;
  /** Leading Cupertino icon (FilledButton.icon etc.). */
  icon?: CupertinoIconName;
  iconSize?: number;
  /** Replaces the icon with a 16 px spinner (label stays; callers pass e.g. "Connecting..."). */
  loading?: boolean;
  fullWidth?: boolean;
  /** `stadium` (theme default) or `rounded` (radius 12, used by several cards). */
  shape?: 'stadium' | 'rounded';
  /** Height in px (Material min 40). */
  height?: number;
  /** Label text style: labelLarge (default), actionLabel (700), titleSmall/titleLarge for large CTAs. */
  labelStyle?: 'label' | 'action' | 'titleSmall' | 'titleLarge';
}

/** Material 3 buttons with Plume's theme (labelLarge Inter 12/600, stadium, 40 px). */
export function Button({
  variant = 'filled',
  tone = 'primary',
  icon,
  iconSize = 18,
  loading = false,
  fullWidth = false,
  shape = 'stadium',
  height,
  labelStyle = 'label',
  className,
  style,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    styles.button,
    styles[variant],
    styles[`tone-${tone}`],
    styles[`label-${labelStyle}`],
    icon || loading ? styles.withIcon : '',
    fullWidth ? styles.fullWidth : '',
    shape === 'rounded' ? styles.rounded : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const merged: CSSProperties = height ? { minHeight: height, ...style } : { ...style };
  return (
    <button type={type} className={classes} style={merged} aria-busy={loading || undefined} {...rest}>
      {loading ? (
        <Spinner size={16} strokeWidth={4} color="currentColor" />
      ) : icon ? (
        <Icon name={icon} size={iconSize} />
      ) : null}
      {children != null && <span className={styles.label}>{children}</span>}
    </button>
  );
}
